import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useBoardsQuery } from '../generated/graphql';
import { BoardCardThumbnail } from '../common/components/boards/BoardCardThumbnail';
import { CreateBoardDialog } from '../common/components/boards/CreateBoardDialog';
import { PageBanner } from '../common/components/ui/PageBanner';

export function BoardsListPage() {
  const { data, loading, error } = useBoardsQuery();
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        maxWidth: 1520,
        py: { xs: 1.5, sm: 1 },
        px: { xs: 0, sm: 2 },
        pr: { xs: 3.5, sm: 2 },
      }}
    >
      <PageBanner
        variant="hero"
        data-testid="boards-header"
        sx={{
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'minmax(0, 1fr) auto',
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{
              letterSpacing: '0.16em',
              fontSize: '0.64rem',
              fontWeight: 700,
            }}
          >
            Your studio · In motion
          </Typography>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontFamily: 'inherit',
              fontWeight: 600,
              fontSize: { xs: 24, md: 28 },
              letterSpacing: '-0.025em',
            }}
          >
            Boards
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.25, display: { xs: 'none', sm: 'block' } }}
          >
            From the first idea to the final master. Keep every project moving.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ justifySelf: 'start' }}
        >
          Create board
        </Button>
      </PageBanner>

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error">
          We couldn't load your boards. Try refreshing.
        </Alert>
      )}

      {!loading && !error && data?.boards.length === 0 && (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" gutterBottom>
            No boards yet
          </Typography>
          <Typography color="text.secondary" mb={3}>
            Organize projects across a pipeline by starting with one board.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Create your first board
          </Button>
        </Box>
      )}

      {!loading && !error && data && data.boards.length > 0 && (
        <>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ fontSize: 13, fontWeight: 600 }}
            >
              Your workspaces
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {data.boards.length}{' '}
              {data.boards.length === 1 ? 'board' : 'boards'}
            </Typography>
          </Stack>
          <Grid container spacing={2}>
            {data.boards.map((board) => (
              <Grid key={board._id} size={{ xs: 12, sm: 6, md: 4 }}>
                <BoardCardThumbnail board={board} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      <CreateBoardDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </Container>
  );
}
